/**
 * Flow Screens Index – links to all flow screens for quick testing.
 * Each link opens the questionnaire at that screen (uses startAt).
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../shared/apiClient';

interface ScreenItem {
  screen_id: string;
  title: string;
  screen_type: string;
  category: string;
  country: string;
  order: number;
}

const COUNTRY_OPTIONS = [
  { code: 'ES', label: 'Spain' },
  { code: 'US', label: 'United States' },
];

/** Spanish autonomous communities for region filter */
const SPANISH_REGIONS = [
  { value: '', label: 'All regions' },
  { value: 'Andalusia', label: 'Andalusia' },
  { value: 'Aragon', label: 'Aragon' },
  { value: 'Asturias', label: 'Asturias' },
  { value: 'Balearic Islands', label: 'Balearic Islands' },
  { value: 'Basque Country', label: 'Basque Country' },
  { value: 'Canary Islands', label: 'Canary Islands' },
  { value: 'Cantabria', label: 'Cantabria' },
  { value: 'Castile and León', label: 'Castile and León' },
  { value: 'Castile-La Mancha', label: 'Castile-La Mancha' },
  { value: 'Catalonia', label: 'Catalonia' },
  { value: 'Extremadura', label: 'Extremadura' },
  { value: 'Galicia', label: 'Galicia' },
  { value: 'La Rioja', label: 'La Rioja' },
  { value: 'Madrid', label: 'Madrid' },
  { value: 'Murcia', label: 'Murcia' },
  { value: 'Navarre', label: 'Navarre' },
  { value: 'Valencia', label: 'Valencia' },
];

/** Display order for sections (matches single "income" category in FIGMA_ALL_SCREENS) */
const SECTION_ORDER = [
  'onboarding',
  'personal',
  'family',
  'housing',
  'income',
  'deductions',
  'review',
  'foreign',
  'submission',
  'other',
];

function formatLabel(cat: string): string {
  return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const FlowScreensIndexPage: React.FC = () => {
  const [country, setCountry] = useState<string>('ES');
  const [region, setRegion] = useState<string>('');
  const [screens, setScreens] = useState<ScreenItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params: Record<string, string> = { country };
    if (region && country === 'ES') params.region = region;
    const query = new URLSearchParams(params).toString();
    get<{ total: number; screens: ScreenItem[] }>(`/flow/screens?${query}`)
      .then((res) => {
        if (!cancelled) {
          setScreens(res.data.screens || []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load screens');
          setScreens([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [country, region]);

  useEffect(() => {
    if (country !== 'ES') setRegion('');
  }, [country]);

  const byCategory = screens.reduce<Record<string, ScreenItem[]>>((acc, s) => {
    const cat = s.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  // Build sections in display order (all income screens have category "income" in API)
  type Section = { key: string; label: string; items: ScreenItem[] };
  const sections: Section[] = [];
  const seen = new Set<string>();

  for (const sectionKey of SECTION_ORDER) {
    const list = byCategory[sectionKey];
    if (list?.length) {
      const sorted = [...list].sort((a, b) => a.order - b.order);
      sections.push({
        key: sectionKey,
        label: formatLabel(sectionKey),
        items: sorted,
      });
      seen.add(sectionKey);
    }
  }

  for (const cat of Object.keys(byCategory).sort()) {
    if (seen.has(cat)) continue;
    const list = byCategory[cat];
    if (list?.length) {
      const sorted = [...list].sort((a, b) => a.order - b.order);
      sections.push({ key: cat, label: formatLabel(cat), items: sorted });
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Flow screens</h1>
          <Link
            to="/questionnaire"
            className="text-sm text-[#07A2A0] hover:underline"
          >
            ← Full questionnaire
          </Link>
        </div>

        <p className="text-gray-600 mb-4">
          Open a screen directly in the questionnaire. Requires{' '}
          <code className="bg-gray-200 px-1 rounded">REACT_APP_FLOW_ALLOW_START_AT</code> not set to{' '}
          <code className="bg-gray-200 px-1 rounded">false</code>.
        </p>

        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium text-gray-700">Country:</label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          {country === 'ES' && (
            <>
              <label className="text-sm font-medium text-gray-700">Region:</label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                title="Filter by autonomous community"
              >
                {SPANISH_REGIONS.map((r) => (
                  <option key={r.value || '_'} value={r.value}>{r.label}</option>
                ))}
              </select>
            </>
          )}
        </div>

        {!loading && !error && country === 'ES' && (
          <p className="text-sm text-gray-500 mb-4">
            Showing {screens.length} screens
            {region ? ` (filtered by ${SPANISH_REGIONS.find((r) => r.value === region)?.label ?? region})` : ''}.
          </p>
        )}

        {loading && (
          <div className="text-gray-500">Loading screens…</div>
        )}
        {error && (
          <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
            {error}
          </div>
        )}
        {!loading && !error && (
          <div className="space-y-6">
            {sections.map((sec) => (
              <section key={sec.key}>
                <h2 className="text-lg font-semibold text-gray-800 mb-2">
                  {sec.label}
                </h2>
                <ul className="space-y-1">
                  {sec.items.map((s) => (
                    <li key={s.screen_id}>
                      <Link
                        to={`/questionnaire?country=${country}&startAt=${encodeURIComponent(s.screen_id)}${region ? `&region=${encodeURIComponent(region)}` : ''}`}
                        className="block py-2 px-3 rounded-lg hover:bg-[#07A2A0]/10 text-[#07A2A0] hover:underline"
                      >
                        <span className="font-medium">{s.title || s.screen_id}</span>
                        <span className="text-gray-400 text-sm ml-2">
                          {s.screen_id} · {s.screen_type}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowScreensIndexPage;
